import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LockIcon, CheckCircleIcon, ClockIcon, BookOpenIcon, ArrowRightIcon, UnlockIcon } from 'lucide-react';
import { CourseInfo } from '../data/coursesData';
import { LockedCourseModal } from './LockedCourseModal';
import { useAuth } from '../contexts/AuthContext';
interface CourseCardProps {
  course: CourseInfo;
  index: number;
  calculatedProgress?: number;
}
export function CourseCard({
  course,
  index,
  calculatedProgress
}: CourseCardProps) {
  const [showModal, setShowModal] = useState(false);
  const {
    isAuthenticated
  } = useAuth();
  const isActive = course.status === 'active';
  const isLocked = course.status === 'locked';
  const isComingSoon = course.status === 'coming-soon';
  // Use calculated progress if provided, otherwise fall back to course.progress
  const progress = calculatedProgress !== undefined ? calculatedProgress : course.progress || 0;
  // Training 1 and 2 require login
  const requiresLogin = !isAuthenticated && (course.id === 'concept-clc' || course.id === 'proceduri-inchiriere');
  const handleClick = (e: React.MouseEvent) => {
    if (isLocked || requiresLogin) {
      e.preventDefault();
      setShowModal(true);
    }
  };
  const cardContent = <>
      <div className="flex items-start justify-between mb-4" data-id="element-690">
        <div className="flex-1" data-id="element-691">
          <div className="flex items-center space-x-2 mb-2" data-id="element-692">
            {isActive && !requiresLogin && <span className="inline-flex items-center space-x-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold" data-id="element-693">
                <CheckCircleIcon className="w-3 h-3" data-id="element-694" />
                <span data-id="element-695">Disponibil acum</span>
              </span>}
            {requiresLogin && <span className="inline-flex items-center space-x-1 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold" data-id="element-696">
                <LockIcon className="w-3 h-3" data-id="element-697" />
                <span data-id="element-698">Necesită logare</span>
              </span>}
            {isLocked && !requiresLogin && <span className="inline-flex items-center space-x-1 bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-xs font-semibold" data-id="element-699">
                <LockIcon className="w-3 h-3" data-id="element-700" />
                <span data-id="element-701">Blocat</span>
              </span>}
            {isComingSoon && <span className="inline-flex items-center space-x-1 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold" data-id="element-702">
                <ClockIcon className="w-3 h-3" data-id="element-703" />
                <span data-id="element-704">În pregătire</span>
              </span>}
          </div>
          <h3 className={`text-xl font-bold mb-2 ${isActive && !requiresLogin ? 'text-white' : 'text-[#132C53]'}`} data-id="element-705">
            {course.title}
          </h3>
          <p className={`text-sm mb-4 ${isActive && !requiresLogin ? 'text-gray-100' : 'text-gray-600'}`} data-id="element-706">
            {course.description}
          </p>
        </div>
        {(isLocked || requiresLogin) && <div className="flex-shrink-0 ml-4" data-id="element-707">
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center" data-id="element-708">
              <LockIcon className="w-6 h-6 text-gray-400" data-id="element-709" />
            </div>
          </div>}
      </div>

      <div className={`flex items-center space-x-4 mb-4 text-sm ${isActive && !requiresLogin ? 'text-gray-200' : 'text-gray-500'}`} data-id="element-710">
        <div className="flex items-center space-x-1" data-id="element-711">
          <BookOpenIcon className="w-4 h-4" data-id="element-712" />
          <span data-id="element-713">
            {course.moduleCount} module • {course.lessonCount} lecții
          </span>
        </div>
      </div>

      {isActive && !requiresLogin && <div className="mb-4" data-id="element-714">
          <div className="flex items-center justify-between text-xs text-gray-200 mb-1" data-id="element-715">
            <span data-id="element-716">Progres</span>
            <span data-id="element-717">{Math.min(progress, 100)}%</span>
          </div>
          <div className="bg-white bg-opacity-20 rounded-full h-2 overflow-hidden" data-id="element-718">
            <motion.div initial={{
          width: 0
        }} animate={{
          width: `${Math.min(progress, 100)}%`
        }} transition={{
          duration: 1,
          delay: 0.5
        }} className="bg-white h-full rounded-full" data-id="element-719" />
          </div>
        </div>}

      {requiresLogin && <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3" data-id="element-720">
          <p className="text-xs text-gray-700 font-medium" data-id="element-721">
            🔐 Pentru a accesa acest training:
          </p>
          <ul className="text-xs text-gray-600 mt-2 space-y-1" data-id="element-722">
            <li data-id="element-723">• Loghează-te în platformă</li>
            <li data-id="element-724">• Sau creează un cont gratuit</li>
          </ul>
        </div>}

      {isLocked && !requiresLogin && <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3" data-id="element-725">
          <p className="text-xs text-gray-700 font-medium" data-id="element-726">
            🔓 Deblochează acest training prin:
          </p>
          <ul className="text-xs text-gray-600 mt-2 space-y-1" data-id="element-727">
            <li data-id="element-728">• Completare onboarding</li>
            <li data-id="element-729">• Finalizare training-uri anterioare</li>
          </ul>
        </div>}

      <div className={`flex items-center justify-between ${isActive && !requiresLogin ? 'text-white' : requiresLogin ? 'text-[#357BEB]' : isLocked ? 'text-[#357BEB]' : 'text-[#357BEB]'}`} data-id="element-730">
        <span className="font-semibold" data-id="element-731">
          {isActive && !requiresLogin && 'Deschide training-ul'}
          {requiresLogin && 'Loghează-te pentru acces'}
          {isLocked && !requiresLogin && 'Deblochează training-ul'}
          {isComingSoon && 'În curând'}
        </span>
        {isActive && !requiresLogin && <ArrowRightIcon className="w-5 h-5" data-id="element-732" />}
        {(requiresLogin || isLocked) && <UnlockIcon className="w-5 h-5" data-id="element-733" />}
      </div>
    </>;
  const cardClasses = `
    block rounded-2xl p-6 shadow-md transition-all duration-300 border-2
    ${isActive && !requiresLogin ? 'bg-gradient-to-br from-[#357BEB] to-[#2868d8] text-white border-[#357BEB] hover:shadow-xl hover:-translate-y-1' : ''}
    ${requiresLogin ? 'bg-white border-blue-200 hover:border-blue-300 hover:shadow-lg cursor-pointer' : ''}
    ${isLocked && !requiresLogin ? 'bg-white border-yellow-200 hover:border-yellow-300 hover:shadow-lg cursor-pointer' : ''}
    ${isComingSoon ? 'bg-gray-50 border-gray-200 opacity-60' : ''}
  `;
  return <>
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.4,
      delay: index * 0.1
    }} data-id="element-734">
        {isActive && isAuthenticated ? <Link to={`/curs/${course.id}`} className={cardClasses} data-id="element-735">
            {cardContent}
          </Link> : <div onClick={handleClick} className={cardClasses} data-id="element-736">
            {cardContent}
          </div>}
      </motion.div>

      <LockedCourseModal isOpen={showModal} onClose={() => setShowModal(false)} courseTitle={course.title} courseId={course.id} data-id="element-737" />
    </>;
}